import { isSuppressed } from '@/types/domain'
import type { CaseLeg } from '@/types/domain'

/** Whether this caller was refused the direction's commercial identity (AD-6).
 *
 *  Read off the PAYLOAD, never re-derived from a role this client happens to
 *  know: the leg's carrier and each unit's carrier and driver — every kind —
 *  are gated by one server-side decision (`toUnitDTOs`), so one hidden field
 *  means the whole group was withheld from this reader.
 *
 *  WHAT IT COSTS IS THE EDIT, and that is the point of having it in one place.
 *  Every write behind a leg form is a whole-record PUT, and a caller who cannot
 *  read the haulier submits those fields blank. Since story B6 the server keeps
 *  the stored carrier, and every known unit's carrier and driver, for such a
 *  caller (`keepIdentity`, decided by `counterparty.read` since S-22c), so a
 *  blank no longer clears them. The leg page still treats such a leg as
 *  read-only for this reader (#133) — no save bar, the drawer, the leg rate and
 *  the unit cards as text, the reason `cases.legs.identityHidden` said once —
 *  rather than taking input no press could send. A new leg carries no markers,
 *  so it stays theirs to create. Lifting the refusal is a product decision, not
 *  a safety one.
 *
 *  It is not a permission check. The server refuses the caller regardless
 *  (AD-21); this stops a well-meaning save from destroying what it cannot see. */
export function legIdentityHidden(leg: CaseLeg): boolean {
  return (
    isSuppressed(leg.carrierName) ||
    isSuppressed(leg.carrierId) ||
    (leg.units ?? []).some(
      (unit) =>
        isSuppressed(unit.carrierName) ||
        isSuppressed(unit.carrierId) ||
        isSuppressed(unit.driverName) ||
        isSuppressed(unit.driverPhone),
    )
  )
}
